import React, {useContext, useEffect, useMemo} from 'react';
import {NestedListWidgetProps} from '../../../@types/components/Form';
import {IEmptyModel} from '../../../@interfaces/IEmptyModel';
import {FormTabsContext} from '../../../contexts/FormTabsContext';
import ControlledList from '../../ControlledList/ControlledList';
import {ListConfigNamedColumn} from '../../../@types/components/List';
import {ControlledListProps} from '../../../@types/components/ControlledList';
import {NestedListFieldWidgetContext} from '../contexts/NestedListFieldWidgetContext';

const emptyObject = {};

export default function NestedListFieldWidget<Model extends IEmptyModel>(props: NestedListWidgetProps<Model>) {
    const tabName = props.tabName;
    const tabsContext = useContext(FormTabsContext);

    const config = props.config;
    const model = props.model;
    const fieldName = props.fieldName;
    const columns = config.columns ?? emptyObject;

    const [rawConfig, records, namedColumns] = useMemo(() => {
        const rawConfig: ControlledListProps<Model>['config'] = {
            columnsNameL10NPrefix: config.columnsNameL10NPrefix,
        };

        const records: Model[] = model?.[fieldName] ?? [];

        const namedColumns = Object.keys(columns ?? {})
                .map<ListConfigNamedColumn>((key) => {
                    return {
                        name: key,
                        ...(columns as any)[key],
                        sortable: false,
                        searchable: false,
                    };
                }, []);

        return [rawConfig, records, namedColumns];
    }, [columns, config, model, fieldName]);

    const shouldDisplayIndicator = props.config.displayNumberOfRecordsInTabLabel === true;
    const indicatorColor = props.config.colorOfNumberOfRecordsInTabLabel ?? 'primary';
    useEffect(() => {
        if (shouldDisplayIndicator && tabsContext && tabName) {
            tabsContext.setBadge(tabName, {
                color: indicatorColor,
                label: String(records.length)
            });
        }
    }, [shouldDisplayIndicator, indicatorColor, tabName, records.length, tabsContext]);

    const List = props.controlledListComponent ?? ControlledList;

    return <NestedListFieldWidgetContext.Provider value={props}>
        <List<Model>
                uniqueListName={`${props.formName}@${props.fieldName}--nested-list-widget`}
                controllerName={props.formName}
                config={rawConfig}
                records={records}
                columns={namedColumns}
                withoutSearchField={true}
                keepInParentBox={true}
                bordered={true}
                className="mb-1"
        />
    </NestedListFieldWidgetContext.Provider>;
}
